import type { ReactNode } from 'react'
import { percent } from '../../lib/format'

// METER_GRADIENT — the orange fill shared by every usage meter.
export const METER_GRADIENT = 'linear-gradient(to right,#fdba74,#f97316)'

// Meter — a labeled used/cap progress bar. A zero (or non-positive) cap is the
// "Unlimited" sentinel and renders as "Unlimited" with no bar (percent()
// returns null). That sentinel is now reserved for a GENUINELY-unlimited
// (future paid) cap — un-resolved/free owners receive concrete Spark caps from
// the backend, so their bars fill normally. `format` controls how `used`/`cap`
// print (e.g. formatBytes vs formatCount).
//
// Optional `secondary` renders an extra line under the bar (used by the
// per-glow Usage tab to show "this glow: X" beneath the account fill). It is
// omitted entirely when not passed, so existing single-figure callers are
// unaffected.
export function Meter({
  label,
  used,
  cap,
  format,
  secondary,
}: {
  label: string
  used: number
  cap: number
  format: (n: number) => string
  secondary?: ReactNode
}) {
  const pct = percent(used, cap)
  const unlimited = pct === null

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <span className="text-sm text-cream/80">{label}</span>
        <span className="font-mono text-sm tabular-nums text-cream">
          {format(used)}{' '}
          <span className="text-muted">
            / {unlimited ? 'Unlimited' : format(cap)}
          </span>
        </span>
      </div>

      {unlimited ? (
        <div className="mt-1 text-xs text-muted">Unlimited</div>
      ) : (
        <>
          <div className="mt-1.5 h-3 w-full overflow-hidden rounded-full bg-surface-2">
            <div
              className="h-full rounded-full"
              style={{ width: `${pct}%`, background: METER_GRADIENT }}
            />
          </div>
          <div className="mt-1 flex justify-between text-xs">
            <span className={pct > 80 ? 'text-glow-500' : 'text-muted'}>
              {pct.toFixed(0)}% used
            </span>
            <span className="font-mono tabular-nums text-muted">
              {format(cap)} cap
            </span>
          </div>
        </>
      )}

      {secondary !== undefined && (
        <div className="mt-1 break-words text-xs text-muted">{secondary}</div>
      )}
    </div>
  )
}
